/**
 * Admin System Info — Sagas.
 * Handles API calls for fetching system version information.
 */

import { call, put, takeEvery, fork } from 'redux-saga/effects';
import { fetchSystemInfo } from '../../infra/api/client';
import { withSagaFlow, emitSagaPoint } from '../qpl-client/saga-helpers';
import {
  FETCH_SYSTEM_INFO_REQUEST,
  fetchSystemInfoSuccess,
  fetchSystemInfoFailure,
} from './ducks';
import type { SystemInfo, SystemInfoApiResponse } from './types';

// ─── Frontend Commit ─────────────────────────────────────

/**
 * Read the frontend commit hash baked in at build time.
 * CRA injects REACT_APP_* env vars into the bundle via process.env.
 * deploy.sh sets REACT_APP_COMMIT_HASH before `npm run build`.
 * Falls back to 'dev' for local development.
 * Extracted as a callable so redux-saga can `call()` it and tests can mock it.
 */
export function getFrontendCommit(): string {
  return process.env.REACT_APP_COMMIT_HASH || 'dev';
}

// ─── Worker Saga ─────────────────────────────────────────

function* handleFetchSystemInfo(action: { type: string }): Generator {
  yield call(withSagaFlow, 'admin_system_info.fetch', ['adminSystemInfo'], action, function* () {
    try {
      const apiData = (yield call(fetchSystemInfo)) as SystemInfoApiResponse;
      yield* emitSagaPoint('api_fetchSystemInfo_ok');
      // Frontend commit comes from build-time env var, not the API —
      // frontend and backend build independently and may differ.
      const frontendCommit = (yield call(getFrontendCommit)) as string;
      const data: SystemInfo = {
        frontend_commit: frontendCommit,
        ...apiData,
      };
      yield put(fetchSystemInfoSuccess(data));
    } catch (error: any) {
      yield put(fetchSystemInfoFailure(error.message || 'Failed to fetch system info'));
      yield* emitSagaPoint('api_fetchSystemInfo_err', { message: error?.message });
    }
  });
}

// ─── Watcher Saga ────────────────────────────────────────

export function* fetchSystemInfoSaga(): Generator {
  yield takeEvery(FETCH_SYSTEM_INFO_REQUEST, handleFetchSystemInfo);
}

// ─── Root Saga ───────────────────────────────────────────

export function* adminSystemInfoSaga(): Generator {
  yield fork(fetchSystemInfoSaga);
}
